import { useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { useUserStats } from "../lib/UserStatsContext";
import { MealIcon, RecipeIcon, MenuIcon, GroceryIcon, CalendarIcon } from "./Icons";
import SurpriseFact from "./SurpriseFact";
import PersonalPatterns from "./PersonalPatterns";
import EnergyROI from "./EnergyROI";
import { isSubscriptionActive } from "../lib/userStats";
import { trackTabNavigation, setUserProfile } from "../lib/analytics";
import { isFeatureEnabled } from "../lib/featureFlags";

type Tab = "home" | "search" | "profile" | "meal" | "recipe" | "menu" | "grocery" | "plate" | "planner" | "admin";

interface DashboardProps {
  onNavigate: (tab: Tab) => void;
}

const QUICK_ACTIONS = [
  { id: "meal" as Tab, icon: MealIcon, label: "Build a meal", description: "Combine foods, see impact" },
  { id: "recipe" as Tab, icon: RecipeIcon, label: "Recipe transformer", description: "GI analysis & smart swaps" },
  { id: "menu" as Tab, icon: MenuIcon, label: "Menu decoder", description: "Scan menus, find best choices" },
  { id: "planner" as Tab, icon: CalendarIcon, label: "Meal planner", description: "AI weekly plans", premium: true },
  { id: "grocery" as Tab, icon: GroceryIcon, label: "Grocery optimizer", description: "Smarter swaps before you shop", premium: true },
];

export default function Dashboard({ onNavigate }: DashboardProps) {
  const { user } = useAuth();
  const { stats, loading } = useUserStats();

  useEffect(() => {
    if (stats) {
      setUserProfile({
        dietType: stats.dietType,
        hasAllergies: (stats.allergies?.length || 0) > 0,
        streakDays: stats.streakDays || 0,
        totalQueries: stats.totalQueries || 0,
        badgeCount: stats.badges?.length || 0,
      });
    }
  }, [stats]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const handleNavigate = (tab: Tab) => {
    trackTabNavigation(tab, "home");
    onNavigate(tab);
  };

  return (
    <div>
      {/* Greeting */}
      <h1 className="text-[22px] font-semibold text-text mb-1">
        {getGreeting()}, {user?.displayName?.split(" ")[0] || "there"}.
      </h1>

      {/* Stats */}
      {!loading && stats && (
        <div className="flex items-baseline gap-4 mt-4 mb-2 font-[family-name:--font-ui]">
          <div className="pr-4 border-r border-border-light">
            <span className="text-lg font-semibold text-text">{stats.streakDays || 0}</span>
            <span className="text-[11px] text-muted ml-1.5">day streak</span>
          </div>
          <div className="pr-4 border-r border-border-light">
            <span className="text-lg font-semibold text-text">{stats.totalQueries || 0}</span>
            <span className="text-[11px] text-muted ml-1.5">queries</span>
          </div>
          <div>
            <span className="text-lg font-semibold text-text">{stats.totalMealsAnalyzed || 0}</span>
            <span className="text-[11px] text-muted ml-1.5">meals</span>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="border-t border-border-light pt-7 mt-6">
        <div className="section-label">Tools</div>
        <div className="space-y-3">
          {QUICK_ACTIONS.filter((a) => isFeatureEnabled(a.id)).map((action) => {
            const Icon = action.icon;
            const isPremiumLocked = action.premium && (!stats || !isSubscriptionActive(stats));
            return (
              <button
                key={action.id}
                onClick={() => handleNavigate(action.id)}
                className="w-full flex items-center gap-3 py-2 text-left group"
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${isPremiumLocked ? "text-muted/40" : "text-muted"}`} />
                <div className="flex-1 min-w-0">
                  <span className={`font-[family-name:--font-ui] text-sm font-medium group-hover:underline ${isPremiumLocked ? "text-muted" : "text-accent"}`}>{action.label}</span>
                  <span className="text-muted text-xs font-[family-name:--font-ui] ml-2">{action.description}</span>
                </div>
                {isPremiumLocked && (
                  <span className="text-[10px] text-muted font-[family-name:--font-ui] font-medium border border-border rounded px-1.5 py-0.5 flex-shrink-0">Premium</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Daily Fact */}
      <div className="border-t border-border-light pt-7 mt-7">
        <SurpriseFact />
      </div>

      {/* Personal Patterns */}
      <div className="border-t border-border-light pt-7 mt-7">
        <PersonalPatterns onNavigate={onNavigate} />
      </div>

      {/* Energy ROI */}
      <div className="border-t border-border-light pt-7 mt-7">
        <EnergyROI />
      </div>
    </div>
  );
}
